/* Imperative Promise-based confirmation dialog (singleton).

   Usage from anywhere (no hooks, no provider needed):

     import { confirm } from "@/lib/confirm";
     const ok = await confirm({
       title: "Excluir cirurgião?",
       description: "Esta ação não pode ser desfeita.",
       confirmText: "Excluir",
       variant: "destructive",
     });
     if (!ok) return;

   Mount <ConfirmRoot /> once at the top of the app.
*/
import React, { useEffect, useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { buttonVariants } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const DEFAULTS = {
  title: "Confirmar ação",
  description: "Tem certeza que deseja prosseguir?",
  confirmText: "Confirmar",
  cancelText: "Cancelar",
  variant: "default",
};

let listener = null;

export function confirm(options = {}) {
  return new Promise((resolve) => {
    if (!listener) {
      // Fallback: if the root isn't mounted (tests, SSR), behave like
      // window.confirm so callers don't break.
      // eslint-disable-next-line no-alert
      const ok = typeof window !== "undefined" ? window.confirm(options.description || options.title || "Confirmar?") : false;
      resolve(ok);
      return;
    }
    listener({ ...DEFAULTS, ...options }, resolve);
  });
}

export function ConfirmRoot() {
  const [opts, setOpts] = useState(null);
  const [resolver, setResolver] = useState(null);

  useEffect(() => {
    listener = (options, resolve) => {
      setOpts(options);
      setResolver(() => resolve);
    };
    return () => {
      listener = null;
    };
  }, []);

  const close = (value) => {
    if (resolver) resolver(value);
    setOpts(null);
    setResolver(null);
  };

  return (
    <AlertDialog
      open={!!opts}
      onOpenChange={(open) => {
        if (!open) close(false);
      }}
    >
      {opts ? (
        <AlertDialogContent data-testid="confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle data-testid="confirm-dialog-title">{opts.title}</AlertDialogTitle>
            <AlertDialogDescription
              data-testid="confirm-dialog-description"
              className="whitespace-pre-line text-sm leading-relaxed"
            >
              {opts.description}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="confirm-dialog-cancel" onClick={() => close(false)}>
              {opts.cancelText}
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-dialog-confirm"
              onClick={() => close(true)}
              className={cn(opts.variant === "destructive" && buttonVariants({ variant: "destructive" }))}
            >
              {opts.confirmText}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      ) : null}
    </AlertDialog>
  );
}
